import { Checkbox, ColorPicker, Input, Label, MultiSelect, NumberInput, Textarea } from "@tellescope/ui";
import { rgbToHex } from "@tellescope/utils";
import { TemplateFormDialogContentsSectionForm } from "./types";
import { useTranslation } from "react-i18next";
import { useMemo } from "react";
import { getEventCellColorsFromHex } from "../../../utils";
import { useCachedTags } from "@tellescope/tanstack-query";

const PRESET_COLOR_RGB: Array<[number, number, number]> = [
  [59, 130, 246],
  [16, 185, 129],
  [139, 92, 246],
  [245, 158, 11],
  [239, 68, 68],
  [6, 182, 212],
  [132, 204, 22],
  [236, 72, 153],
  [249, 115, 22],
  [99, 102, 241],
];

const PRESET_COLORS = PRESET_COLOR_RGB.map(([r, g, b]) => rgbToHex(r, g, b));

export interface InfoSectionProps {
  form: TemplateFormDialogContentsSectionForm<
    | "title"
    | "durationInMinutes"
    | "color"
    | "description"
    | "enableVideoCall"
    | "publicRead"
    | "confirmationEmailDisabled"
    | "confirmationSMSDisabled"
    | "portalSettings.hideUsers"
    | "requiresEnduser"
    | "enduserAttendeeLimit"
    | "bufferStartMinutes"
    | "bufferEndMinutes"
    | "preventCancelMinutesInAdvance"
    | "preventRescheduleMinutesInAdvance"
    | "replaceHostOnReschedule"
    | "tags"
  >;
}

export const InfoSection = ({ form }: InfoSectionProps) => {
  const { t } = useTranslation("calendar");

  const colorPreview = useMemo(() => getEventCellColorsFromHex(form.values.color), [form.values.color]);

  const { data: availableTags } = useCachedTags({
    model: "calendar_event_templates",
  });

  return (
    <section className="space-y-6">
      <span className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="title" required>
            {t("Title")}
          </Label>
          <Input id="title" {...form.getFieldProps("title")} placeholder={t("Template name")} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="durationInMinutes" required>
            {t("Duration (minutes)")}
          </Label>
          <NumberInput
            id="durationInMinutes"
            value={form.values.durationInMinutes}
            onChange={(val) => form.setFieldValue("durationInMinutes", val)}
          />
        </div>
      </span>
      <div className="space-y-2">
        <span className="flex items-center justify-between">
          <Label htmlFor="color">{t("Color")}</Label>
          <div
            className="text-xs border rounded-sm py-0.5 px-2 text-nowrap flex items-center justify-between gap-2"
            style={{
              background: colorPreview.backgroundColor,
              borderColor: colorPreview.borderColor,
              color: colorPreview.textColor,
            }}
          >
            {t("Color Preview")}
          </div>
        </span>
        <ColorPicker
          id="color"
          {...form.getCustomFieldProps("color")}
          presetColors={PRESET_COLORS}
          swatchClassName="rounded-sm"
          clearable
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="description">{t("Description")}</Label>
        <Textarea id="description" {...form.getFieldProps("description")} placeholder={t("Optional Description")} />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <span className="flex items-center gap-2">
          <Checkbox
            id="enableVideoCall"
            checked={form.values.enableVideoCall}
            onCheckedChange={(newVal) => form.setFieldValue("enableVideoCall", !!newVal)}
          />
          <Label htmlFor="enableVideoCall">{t("Include Video Call")}</Label>
        </span>
        <span className="flex items-center gap-2">
          <Checkbox
            id="confirmationSMSDisabled"
            checked={form.values.confirmationSMSDisabled}
            onCheckedChange={(newVal) => form.setFieldValue("confirmationSMSDisabled", !!newVal)}
          />
          <Label htmlFor="confirmationSMSDisabled">{t("Disable Confirmation SMS")}</Label>
        </span>
        <span className="flex items-center gap-2">
          <Checkbox
            id="publicRead"
            checked={form.values.publicRead}
            onCheckedChange={(newVal) => form.setFieldValue("publicRead", !!newVal)}
          />
          <Label htmlFor="publicRead">{t("Show Host URL")}</Label>
        </span>
        <span className="flex items-center gap-2">
          <Checkbox
            id="portalSettings.hideUsers"
            checked={form.values["portalSettings.hideUsers"]}
            onCheckedChange={(newVal) => form.setFieldValue("portalSettings.hideUsers", !!newVal)}
          />
          <Label htmlFor="portalSettings.hideUsers">{t("Hide Care Team from Portal")}</Label>
        </span>
        <span className="flex items-center gap-2">
          <Checkbox
            id="confirmationEmailDisabled"
            checked={form.values.confirmationEmailDisabled}
            onCheckedChange={(newVal) => form.setFieldValue("confirmationEmailDisabled", !!newVal)}
          />
          <Label htmlFor="confirmationEmailDisabled">{t("Disable Confirmation Email")}</Label>
        </span>
        <span className="flex items-center gap-2">
          <Checkbox
            id="requiresEnduser"
            checked={form.values.requiresEnduser}
            onCheckedChange={(newVal) => form.setFieldValue("requiresEnduser", !!newVal)}
          />
          <Label htmlFor="requiresEnduser">{t("Require at least 1 contact attendee")}</Label>
        </span>
      </div>
      <div className="space-y-2">
        <Label htmlFor="enduserAttendeeLimit">{t("Max Attendee Count")}</Label>
        <NumberInput
          id="enduserAttendeeLimit"
          value={form.values.enduserAttendeeLimit}
          onChange={(newVal) => form.setFieldValue("enduserAttendeeLimit", newVal)}
          placeholder={t("Leave empty for unlimited")}
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="bufferStartMinutes">{t("Pre-Event Buffer (minutes)")}</Label>
          <NumberInput
            id="bufferStartMinutes"
            value={form.values.bufferStartMinutes ?? 0}
            onChange={(newVal) => form.setFieldValue("bufferStartMinutes", newVal)}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="bufferEndMinutes">{t("Post-Event Buffer (minutes)")}</Label>
          <NumberInput
            id="bufferEndMinutes"
            value={form.values.bufferEndMinutes ?? 0}
            onChange={(newVal) => form.setFieldValue("bufferEndMinutes", newVal)}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="preventCancelMinutesInAdvance">{t("Prevent Cancel (minutes before start)")}</Label>
          <NumberInput
            id="preventCancelMinutesInAdvance"
            value={form.values.preventCancelMinutesInAdvance ?? 0}
            onChange={(newVal) => form.setFieldValue("preventCancelMinutesInAdvance", newVal)}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="preventRescheduleMinutesInAdvance">{t("Prevent Reschedule (minutes before start)")}</Label>
          <NumberInput
            id="preventRescheduleMinutesInAdvance"
            value={form.values.preventRescheduleMinutesInAdvance ?? 0}
            onChange={(newVal) => form.setFieldValue("preventRescheduleMinutesInAdvance", newVal)}
          />
        </div>
      </div>
      <span className="flex items-center gap-2">
        <Checkbox
          id="replaceHostOnReschedule"
          checked={form.values.replaceHostOnReschedule}
          onCheckedChange={(checked) => form.setFieldValue("replaceHostOnReschedule", !!checked)}
        />
        <Label htmlFor="replaceHostOnReschedule">{t("Replace Host on Reschedule")}</Label>
      </span>
      <div className="space-y-2">
        <Label htmlFor="tags">{t("Tags")}</Label>
        <MultiSelect
          className="h-auto py-0 w-full"
          value={form.values.tags ?? []}
          onValueChange={(tags) => form.setFieldValue("tags", tags)}
          options={Array.from(new Set([...availableTags, ...(form.values.tags ?? [])])).map((tag) => ({
            label: tag,
            value: tag,
          }))}
          creator={{
            render: (inputValue) => <p>{t("Add '{{tag}}' tag", { tag: inputValue })}</p>,
            transform: (inputValue) => inputValue,
            onCreate: (val) =>
              form.setFieldValue("tags", Array.from(new Set([...(form.values.tags ?? []), val.value]))),
          }}
        />
      </div>
    </section>
  );
};
